import React, { useEffect, useState } from "react";
import { ChevronDown, ListChecks, Paperclip, X, Loader2, Sparkles } from "lucide-react";
import { httpClient } from "../../../../HttpClient";
import { uploadFileToBlob } from "../../../../utils/uploadFileToBlob";
import { showStatusToast } from "../../../../components/ui/Toast";
import { useEtatGlobal } from "../../../EtatGlobal.js";

// Suivi > Bilan > onglet Diligences LAB : diligences retenues dans le module
// KYC/LAB du client, reprises dans chaque exercice avec un suivi propre à
// l'année (statut, conclusion, réalisation, revue, pièce jointe) — demande
// d'Anthony du 13/10/2026. Backend : controllers/GI/kycDiligencesAnneeController.js.

const API = "https://backend-myalfa.vercel.app/api/gi/kyc";
const UPLOAD_TOKEN = "https://backend-myalfa.vercel.app/api/suiviBilan/upload-token";
const STATUTS = [
  { id: "a_faire", label: "À faire", style: "bg-slate-50 text-slate-700 border-slate-300" },
  { id: "en_cours", label: "En cours", style: "bg-amber-50 text-amber-700 border-amber-200" },
  { id: "a_completer", label: "À compléter", style: "bg-orange-50 text-orange-700 border-orange-200" },
  { id: "realisee", label: "Réalisée", style: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { id: "revue", label: "Revue", style: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { id: "non_applicable", label: "Non applicable cette année", style: "bg-slate-100 text-slate-500 border-slate-200" },
];
const STYLE = Object.fromEntries(STATUTS.map((s) => [s.id, s.style]));
const FAITES = new Set(["realisee", "revue", "non_applicable"]);
const FICHE = [
  ["travaux", "Travaux à réaliser"],
  ["pieces", "Pièces / preuve attendue"],
  ["signaux", "Signaux d'alerte"],
  ["conclusion", "Conclusion attendue"],
];
const CHAMP = "h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#6e1a36]/20";
const auj = () => new Date().toISOString().slice(0, 10);

function Ligne({ d, users, utilisateur, enregistrer }) {
  const [ouvert, setOuvert] = useState(false);
  const [envoi, setEnvoi] = useState(null);
  const s = d.suivi || {};
  const statut = s.statut || "a_faire";
  const nomUser = (id) => {
    const u = users.find((x) => String(x.id) === String(id));
    return u ? `${u.name || ""} ${u.lastName || ""}`.trim() : "";
  };

  // Réalisée / revue sans auteur ni date : renseignés avec soi-même et aujourd'hui.
  const changerStatut = (v) => {
    const patch = { statut: v };
    if (v === "realisee" && !s.executantId) Object.assign(patch, { executantId: utilisateur?.id, dateRealisation: s.dateRealisation || auj() });
    if (v === "revue" && !s.reviseurId) Object.assign(patch, { reviseurId: utilisateur?.id, dateRevue: s.dateRevue || auj() });
    enregistrer(d, patch);
  };

  const deposer = async (fichier) => {
    if (!fichier) return;
    setEnvoi(0);
    try {
      const url = await uploadFileToBlob(fichier, UPLOAD_TOKEN, ({ percentage }) => setEnvoi(Math.round(percentage)));
      await enregistrer(d, { pieceUrl: url, pieceNom: fichier.name });
    } catch (e) {
      showStatusToast({ status: "error", titre: "Erreur", message: "Le fichier n'a pas pu être déposé." });
    } finally {
      setEnvoi(null);
    }
  };

  return (
    <div className={`rounded-lg border ${FAITES.has(statut) ? "border-emerald-100 bg-emerald-50/20" : "border-slate-200 bg-white"}`}>
      <div className="flex items-center gap-3 px-3 py-2">
        <button onClick={() => setOuvert((o) => !o)} className="flex items-start gap-2 flex-1 min-w-0 text-left" aria-expanded={ouvert}>
          <ChevronDown className={`w-4 h-4 mt-0.5 shrink-0 text-slate-400 transition-transform ${ouvert ? "rotate-180" : ""}`} />
          <span className="min-w-0">
            <span className="text-sm text-slate-800">
              {d.code && <span className="text-[11px] font-semibold text-slate-400 mr-1.5">{d.code}</span>}
              {d.libelle}
              {d.origine === "ia" && <Sparkles className="inline w-3 h-3 ml-1 text-[#9B1B5D]" aria-label="Suggérée par l'IA" />}
            </span>
            <span className="block text-[11px] text-slate-400">
              {d.type === "prealable" ? "Préalable" : "Suivi"}
              {s.executantId && ` · réalisée par ${nomUser(s.executantId)}${s.dateRealisation ? ` le ${new Date(s.dateRealisation).toLocaleDateString("fr-FR")}` : ""}`}
              {s.reviseurId && ` · revue par ${nomUser(s.reviseurId)}`}
              {s.pieceUrl && " · pièce jointe"}
            </span>
          </span>
        </button>
        <select value={statut} onChange={(e) => changerStatut(e.target.value)} className={`${CHAMP} w-48 font-medium ${STYLE[statut]}`} aria-label="Statut de l'exercice">
          {STATUTS.map((x) => (
            <option key={x.id} value={x.id}>{x.label}</option>
          ))}
        </select>
      </div>
      {ouvert && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-3">
          {d.fiche && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-1.5 rounded-md bg-slate-50 p-2.5">
              {FICHE.filter(([k]) => d.fiche[k]).map(([k, l]) => (
                <div key={k}>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 m-0">{l}</p>
                  <p className="text-[11px] text-slate-600 m-0 whitespace-pre-line">{d.fiche[k]}</p>
                </div>
              ))}
            </div>
          )}
          <label className="block">
            <span className="text-xs font-semibold text-slate-500">Conclusion de l'exercice</span>
            <textarea
              key={s.conclusion || ""}
              defaultValue={s.conclusion || ""}
              rows={2}
              onBlur={(e) => e.target.value.trim() !== (s.conclusion || "") && enregistrer(d, { conclusion: e.target.value })}
              placeholder="Cohérent / à compléter / anomalie à escalader / non applicable motivé — décrire les travaux et pièces examinés"
              className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#6e1a36]/20"
            />
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              ["Réalisée par", "executantId", "dateRealisation"],
              ["Revue par", "reviseurId", "dateRevue"],
            ].map(([libelle, champUser, champDate]) => (
              <div key={champUser}>
                <span className="text-xs font-semibold text-slate-500">{libelle}</span>
                <div className="flex gap-2 mt-1">
                  <select value={s[champUser] || ""} onChange={(e) => enregistrer(d, { [champUser]: e.target.value || null })} className={`${CHAMP} flex-1`} aria-label={libelle}>
                    <option value="">—</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name} {u.lastName}</option>
                    ))}
                  </select>
                  <input type="date" value={s[champDate] || ""} onChange={(e) => enregistrer(d, { [champDate]: e.target.value || null })} className={`${CHAMP} w-36`} aria-label={`Date — ${libelle}`} />
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-500">Pièce jointe</span>
            {s.pieceUrl ? (
              <>
                <a href={s.pieceUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-[#9B1B5D] hover:underline">
                  <Paperclip className="w-3.5 h-3.5" /> {s.pieceNom || "Ouvrir le document"}
                </a>
                <button onClick={() => enregistrer(d, { pieceUrl: null })} className="p-0.5 text-slate-300 hover:text-rose-600" aria-label="Retirer la pièce jointe" title="Retirer">
                  <X className="w-3.5 h-3.5" />
                </button>
              </>
            ) : envoi !== null ? (
              <span className="flex items-center gap-1.5 text-xs text-slate-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Dépôt en cours… {envoi} %
              </span>
            ) : (
              <label className="flex items-center gap-1 h-7 px-2.5 rounded-md text-xs border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer">
                <Paperclip className="w-3.5 h-3.5" /> Joindre un fichier
                <input type="file" className="hidden" accept=".pdf,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.heic" onChange={(e) => deposer(e.target.files?.[0])} />
              </label>
            )}
          </div>
          {s.modifiePar && <p className="text-[10px] text-slate-400 m-0">Dernière modification : {s.modifiePar}{s.majLe ? `, le ${new Date(s.majLe).toLocaleDateString("fr-FR")}` : ""}</p>}
        </div>
      )}
    </div>
  );
}

export default function DiligencesLabAnnee({ clientId, annee }) {
  const { users = [], utilisateur } = useEtatGlobal();
  const [liste, setListe] = useState(null);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    if (!clientId || !annee) return undefined;
    let actif = true;
    setListe(null);
    setErreur(null);
    httpClient
      .get(`${API}/${clientId}/diligences/annee/${annee}`)
      .then(({ data }) => actif && setListe(data.diligences))
      .catch(() => actif && setErreur("Les diligences de l'exercice n'ont pas pu être chargées."));
    return () => {
      actif = false;
    };
  }, [clientId, annee]);

  // Optimiste : affiché tout de suite, annulé si le serveur refuse.
  const enregistrer = async (d, patch) => {
    const avant = d.suivi;
    const remplacer = (suivi) => setListe((l) => l.map((x) => (x.id === d.id ? { ...x, suivi } : x)));
    remplacer({ ...(avant || { statut: "a_faire" }), ...patch });
    try {
      const { data } = await httpClient.put(`${API}/${clientId}/diligences/annee/${annee}/${encodeURIComponent(d.id)}`, patch);
      remplacer(data.suivi);
    } catch (err) {
      remplacer(avant);
      showStatusToast({ status: "error", titre: "Erreur", message: err?.response?.data?.error || "La modification n'a pas pu être enregistrée." });
    }
  };

  const faites = (liste || []).filter((d) => FAITES.has(d.suivi?.statut)).length;
  const total = (liste || []).length;

  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <h4 className="text-sm font-semibold text-slate-900 m-0 flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-[#9B1B5D]" />
          Diligences du dossier — exercice {annee}
        </h4>
        {total > 0 && (
          <span className="flex items-center gap-2 ml-auto text-xs text-slate-500">
            <span className="w-28 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <span className="block h-full bg-emerald-500 transition-all" style={{ width: `${Math.round((faites / total) * 100)}%` }} />
            </span>
            {faites} / {total} réalisée{faites > 1 ? "s" : ""}
          </span>
        )}
      </div>
      <p className="text-[11px] text-slate-400 mt-0 mb-3">
        Diligences retenues dans le module KYC/LAB du client, reprises chaque année. Pour en ajouter ou en retirer, passez par la fiche client › GI/LAB › Risques & vigilance.
      </p>
      {erreur && <p className="text-xs text-rose-600 m-0">{erreur}</p>}
      {!erreur && liste === null && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => <div key={i} className="h-11 rounded-lg bg-slate-100 animate-pulse" />)}
        </div>
      )}
      {liste && total === 0 && <p className="text-xs text-slate-500 italic m-0">Aucune diligence retenue dans le module KYC/LAB de ce client.</p>}
      {liste && total > 0 && (
        <div className="space-y-2">
          {liste.map((d) => (
            <Ligne key={d.id} d={d} users={users} utilisateur={utilisateur} enregistrer={enregistrer} />
          ))}
        </div>
      )}
    </div>
  );
}
